/**
 * useTemplateThumbnails
 * ---------------------
 * Renders each template's sections off-screen into a hidden DOM node,
 * captures them with html2canvas, and caches the resulting data URLs in
 * localStorage so the work only happens once (or when a template changes).
 *
 * Usage:
 *   const thumbnails = useTemplateThumbnails(PAGE_TEMPLATES);
 *   // thumbnails['webinar-conference'] === 'data:image/png;base64,...' | null
 *
 * Requirements:
 *   npm install html2canvas
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom/client';
import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import { PreviewCtx, CtxMenuCtx, DragCtx } from '../../contexts';
import { Section } from '../../canvas/Section';

// ── Cache key helpers ─────────────────────────────────────────────────────────
const CACHE_VERSION = 'v1'; // bump this to invalidate all cached thumbnails
const cacheKey = (tplId) => `gv-tpl-thumb-${CACHE_VERSION}-${tplId}`;

function readCache(tplId) {
  try { return localStorage.getItem(cacheKey(tplId)) || null; } catch { return null; }
}

function writeCache(tplId, dataUrl) {
  try { localStorage.setItem(cacheKey(tplId), dataUrl); } catch {}
}

// ── Noop stubs for required Section callbacks ─────────────────────────────────
const NOOP   = () => {};
const NOOP_REF = { current: null };

// Minimal DragCtx value that satisfies Section without enabling any drag ops
const DRAG_CTX = {
  drag: { current: null },
  sectionDrag: { current: null },
  breakpointRef: { current: 'desktop' },
  resetAll: NOOP,
  _resets: new Set(),
};

// Minimal CtxMenuCtx value
const CTX_MENU = { open: NOOP };

// ── Minimal MUI theme (inherits defaults — keeps fonts & spacing consistent) ──
const PREVIEW_THEME = createTheme();

// ── Off-screen renderer ───────────────────────────────────────────────────────
/**
 * Creates an off-screen container, renders the given sections into it,
 * waits for images to load, captures it with html2canvas, then cleans up.
 *
 * Returns a Promise<string | null> (data URL or null on failure).
 */
async function renderTemplateThumbnail(sections, thumbWidth = 1200, thumbHeight = 800) {
  // Lazy-load html2canvas so the app doesn't break if it's not installed
  let html2canvas;
  try {
    html2canvas = (await import('html2canvas')).default;
  } catch {
    console.warn('[useTemplateThumbnails] html2canvas not installed. Run: npm install html2canvas');
    return null;
  }

  // Create a hidden fixed container sized to our virtual "viewport"
  const container = document.createElement('div');
  Object.assign(container.style, {
    position:   'fixed',
    top:        '-99999px',
    left:       '-99999px',
    width:      `${thumbWidth}px`,
    height:     `${thumbHeight}px`,
    overflow:   'hidden',
    zIndex:     '-1',
    background: '#ffffff',
    pointerEvents: 'none',
  });
  document.body.appendChild(container);

  // Render the sections into the container via a React root
  const contentSections = sections.filter(s => s.type !== 'header' && s.type !== 'footer');

  const Preview = () => (
    <ThemeProvider theme={PREVIEW_THEME}>
      <CssBaseline />
      <PreviewCtx.Provider value={true}>
        <CtxMenuCtx.Provider value={CTX_MENU}>
          <DragCtx.Provider value={DRAG_CTX}>
            <Box sx={{ background: '#fff', width: '100%' }}>
              {contentSections.map((section, idx) => (
                <Section
                  key={section.id}
                  section={section}
                  sectionIdx={idx}
                  sectionsCount={contentSections.length}
                  selectedId={null}
                  selectedSectionId={null}
                  onSelectSection={NOOP}
                  onSelect={NOOP}
                  onChange={NOOP}
                  onDelete={NOOP}
                  onDrop={NOOP}
                  onSelectChild={NOOP}
                  onReorder={NOOP}
                  onSectionReorder={NOOP}
                  onSectionDuplicate={NOOP}
                  onSectionCopy={NOOP}
                  onSectionPaste={NOOP}
                  sectionClipboard={null}
                  onUpdateEl={NOOP}
                  onDeleteEl={NOOP}
                  onMoveChildToSection={NOOP}
                  onMoveChildToContainer={NOOP}
                  onInsertNewChildAt={NOOP}
                  onMoveTopLevelIntoContainer={NOOP}
                  onRename={NOOP}
                  onMergeAsRow={NOOP}
                  onSplitDropIntoEmpty={NOOP}
                />
              ))}
            </Box>
          </DragCtx.Provider>
        </CtxMenuCtx.Provider>
      </PreviewCtx.Provider>
    </ThemeProvider>
  );

  const root = ReactDOM.createRoot(container);

  await new Promise(resolve => {
    root.render(<Preview />);
    // Give React a tick to commit the render, then wait for images
    setTimeout(async () => {
      // Wait for all images in the container to load (max 3s)
      const imgs = Array.from(container.querySelectorAll('img'));
      await Promise.all(
        imgs.map(img =>
          img.complete
            ? Promise.resolve()
            : new Promise(res => {
                const timer = setTimeout(res, 3000);
                img.onload  = () => { clearTimeout(timer); res(); };
                img.onerror = () => { clearTimeout(timer); res(); };
              })
        )
      );
      resolve();
    }, 300);
  });

  let dataUrl = null;
  try {
    const canvas = await html2canvas(container, {
      width:             thumbWidth,
      height:            thumbHeight,
      scale:             0.5,          // capture at 0.5x → 600×400 output
      useCORS:           true,
      allowTaint:        true,
      backgroundColor:   '#ffffff',
      logging:           false,
      windowWidth:       thumbWidth,
      windowHeight:      thumbHeight,
    });
    dataUrl = canvas.toDataURL('image/jpeg', 0.82);
  } catch (err) {
    console.warn('[useTemplateThumbnails] html2canvas capture failed:', err);
  }

  // Clean up
  root.unmount();
  document.body.removeChild(container);

  return dataUrl;
}

// ── Hook ──────────────────────────────────────────────────────────────────────
/**
 * @param {Array} templates  — PAGE_TEMPLATES array
 * @returns {Object}  map of templateId → dataUrl string | null
 *                    null means "not yet generated" (show fallback sketch)
 */
export function useTemplateThumbnails(templates) {
  const [thumbs, setThumbs] = useState(() => {
    // Initialise from cache synchronously so cached thumbnails show instantly
    const map = {};
    for (const tpl of templates) {
      map[tpl.id] = readCache(tpl.id);
    }
    return map;
  });

  const generating = useRef(new Set());

  const generateOne = useCallback(async (tpl) => {
    if (generating.current.has(tpl.id)) return;
    generating.current.add(tpl.id);

    const dataUrl = await renderTemplateThumbnail(tpl.sections);

    if (dataUrl) {
      writeCache(tpl.id, dataUrl);
      setThumbs(prev => ({ ...prev, [tpl.id]: dataUrl }));
    }
    generating.current.delete(tpl.id);
  }, []);

  useEffect(() => {
    for (const tpl of templates) {
      // Only generate if not already in cache
      if (!thumbs[tpl.id]) {
        generateOne(tpl);
      }
    }
  }, []); // eslint-disable-line 

  return thumbs;
}

/**
 * Clears all cached thumbnails from localStorage.
 * Call this after updating a template definition.
 */
export function clearTemplateThumbnailCache() {
  try {
    Object.keys(localStorage)
      .filter(k => k.startsWith('gv-tpl-thumb-'))
      .forEach(k => localStorage.removeItem(k));
  } catch {}
}